import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, PawPrint, Pencil } from "lucide-react";
import { IconButton, Typography } from "@akuna/ui";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { PetReservationSummary } from "./usePetReservationSummary";
import { PetConditionsSection } from "./sections/PetConditionsSection";
import { PetAlertsSection } from "./sections/PetAlertsSection";
import { PetStoredItemsSection } from "./sections/PetStoredItemsSection";
import { PetApplicationsSection } from "./sections/PetApplicationsSection";
import { PetRelationshipsSection } from "./sections/PetRelationshipsSection";
import { PetStayNotesSection } from "./sections/PetStayNotesSection";
import { PetProfileReadSummary } from "./PetProfileReadSummary";
import { ReservationPlanAlert } from "./ReservationPlanAlert";
import type { StayPlanStatus } from "@/hooks/useStayPlanStatus";

export type PetProfileCardProps = {
  petId: string;
  customerId: string | null;
  summary: PetReservationSummary | null | undefined;
  planStatus?: StayPlanStatus | null;
  planDay?: string;
  alertContent?: ReactNode;
  defaultOpen?: boolean;
};

/**
 * As seções vivem soltas no fluxo do modal — sem card em volta. O header dá
 * nome ao conjunto, para separá-lo da comanda e das atividades do dia, e serve
 * de gatilho para colapsar a ficha (fechada por padrão no checkout).
 */
export function PetProfileCard({ petId, customerId, summary, planStatus = null, planDay = "", alertContent, defaultOpen = true }: PetProfileCardProps) {
  const { t } = useTranslation("daycareHotel");
  const [open, setOpen] = useState(defaultOpen);
  const [isEditing, setIsEditing] = useState(false);

  // `StayPetDialog` reaproveita a mesma instância ao trocar de acompanhamento
  // para checkout (é o mesmo Dialog, não um novo) — sem isso o `useState`
  // inicial não seria reavaliado e a ficha ficaria aberta no checkout.
  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  if (!summary) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="mb-2.5 flex items-center justify-between gap-1">
        <div className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <PawPrint aria-hidden="true" size={18} className="text-akuna-blue" />
          <Typography variant="h5">{t("petProfile.title")}</Typography>
        </div>
        <div className="flex min-w-0 items-center gap-2">
          <IconButton
            variant={isEditing ? "secondary" : "ghost"}
            size="icon-sm"
            label={t(isEditing ? "petProfile.doneEditing" : "petProfile.edit")}
            onClick={() => setIsEditing((current) => !current)}
          >
            {isEditing ? <Check className="h-4 w-4" aria-hidden="true" /> : <Pencil className="h-4 w-4" aria-hidden="true" />}
          </IconButton>
          <CollapsibleTrigger asChild>
              <ChevronDown
                aria-hidden="true"
                size={18}
                className={cn("ml-auto shrink-0 text-muted-foreground transition-transform", open && "rotate-180")}
              />
          </CollapsibleTrigger>
        </div>
      </div>

      <CollapsibleContent className={isEditing ? undefined : "grid gap-3"}>
        {isEditing ? <div data-testid="pet-profile-editor" className="grid gap-2 sm:grid-cols-2">
          <PetApplicationsSection petId={petId} applications={summary.applications} showEmpty />
          <PetConditionsSection petId={petId} kind="allergy" allergies={summary.allergies} temperaments={summary.temperaments} showEmpty />
          <PetConditionsSection petId={petId} kind="temperament" allergies={summary.allergies} temperaments={summary.temperaments} showEmpty />
          <PetAlertsSection petId={petId} customerId={customerId} alerts={summary.alerts} showEmpty />
          <PetStoredItemsSection petId={petId} storedItems={summary.storedItems} showEmpty />
          <PetRelationshipsSection petId={petId} customerId={customerId} relationships={summary.relationships} showEmpty />
          <PetStayNotesSection petId={petId} notes={summary.daycareHotelNotes} showEmpty />
        </div> : <>
          {alertContent ?? <ReservationPlanAlert status={planStatus} petId={petId} day={planDay} />}
          <PetProfileReadSummary summary={summary} />
        </>}
      </CollapsibleContent>
    </Collapsible>
  );
}
